import { useCallback, useContext, useEffect, useState } from "react";
import styled from "styled-components";
import Header from "../header/Header";
import { useTranslation } from "react-i18next";
import { ProductContext } from "../../context/ProductContext";
import ProductSlider from "../../components/product/ProductSlider";
import Footer from "../footer/Footer";
//import Subscription from "../footer/Subscription";
import ProductSliderHorizontal from "../../components/product/RecommendedSlider";
import ProductCard from "../../components/product/ProductCard";
import { Link, useNavigate } from "react-router-dom";
import { useNavigation } from "../../utils/navigation";
import APIService from "../../services/APIService";

const Container = styled.article`
  color: ${(props) => props.theme.textColor};
  padding-top: 72px;
`;
const Section = styled.section`
  @media (min-width: 765px) {
    margin-top: 80px;
  }
`;
const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px;
  @media (min-width: 765px) and (max-width: 1024px) {
    grid-template-columns: repeat(3, 1fr);
  }
  @media (min-width: 1025px) {
    grid-template-columns: repeat(4, 1fr);
  }
  @media (min-width: 1280px) {
    grid-template-columns: repeat(5, 1fr);
  }
`;

const CustomLink = styled.button`
  text-align: center;
  //width: 100%;
  display: block;
  color: var(--text-color);
  font-size: var(--font-size-base);
  margin: 14px auto;
  background-color: var(--bg-200);
  &:hover {
    background-color: var(--bg-200);
  }

  border-radius: 20px;
  border-color: var(--bg-300);
  text-transform: capitalize;
`;

const ProductGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px; /* Razmak između proizvoda */
  margin-top: 15px;

  @media (min-width: 765px) and (max-width: 1024px) {
    grid-template-columns: repeat(3, 1fr);
  }
  @media (min-width: 1025px) {
    grid-template-columns: repeat(5, 1fr);
  }
  @media (min-width: 1280px) {
    grid-template-columns: repeat(6, 1fr);
  }
`;

const LoadMoreButton = styled.button`
  grid-column: span 2; /* Dugme zauzima celu širinu */

  margin-top: 15px;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  text-align: center;
  @media (min-width: 765px) {
    flex-direction: column;
    align-items: flex-start;
    justify-content: flex-end;
    grid-column: span 1;
    margin-top: 0px;
    height: 80%;
  }
`;
const ButtonText = styled.div`
  font-size: 50px;
`;
const BATCH_SIZE = 3; // broj brendova koje učitamo odjednom
const SCROLL_OFFSET = 300;
const BrandsMain = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { goToCategory } = useNavigation();
  const { products, loadProducts, categories } = useContext(ProductContext);
  const [showAll, setShowAll] = useState(false);
  const [showMore, setShowMore] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth <= 768); // Podesi širinu prema tvojim potrebama za mobilne uređaje
    };

    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);

    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  const handleShowAll = () => {
    setShowAll((prevState) => !prevState);
  };
  const handleShowMore = () => {
    setShowMore((prevState) => !prevState);
  };

  const [visibleCount, setVisibleCount] = useState(BATCH_SIZE);
  const [brandData, setBrandData] = useState({});

  // Fetch proizvode samo za kategorije koje još nismo učitali
  const fetchNewBatches = useCallback(async () => {
    const toLoad = categories
      .slice(0, visibleCount)
      .filter((cat) => !brandData[cat.slug]);

    if (toLoad.length === 0) return;

    try {
      const updated = { ...brandData };
      for (const { slug } of toLoad) {
        const resp = await APIService.GetProductsByCategory(slug);
        updated[slug] = resp.products;
      }
      setBrandData(updated);
    } catch (err) {
      console.error("Greška pri fetch‑u proizvoda:", err);
    }
  }, [categories, visibleCount, brandData]);

  // Učitaj inicijalne i nove batch‑eve kad se visibleCount ili categories promene
  useEffect(() => {
    fetchNewBatches();
  }, [fetchNewBatches]);

  // Scroll‑listener za infinite scroll
  useEffect(() => {
    const onScroll = () => {
      const scrollPos = window.innerHeight + window.scrollY;
      const bottomPos = document.body.offsetHeight - SCROLL_OFFSET;
      if (scrollPos >= bottomPos && visibleCount < categories.length) {
        setVisibleCount((count) =>
          Math.min(count + BATCH_SIZE, categories.length)
        );
      }
    };

    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, [visibleCount, categories.length]);

  return (
    <>
      <Header />
      <Container>
        <Section>
          <BrandsSection>
            <Title>{t("SHOP.BRANDS.TITLE")}</Title>
            <Description
              dangerouslySetInnerHTML={{
                __html: t("SHOP.BRANDS.DESCRIPTION"),
              }}
              $showMore={showMore}
            />
            <Button onClick={handleShowMore}>
              {showMore ? (
                <>
                  {t("SHOP.BRANDS.SEE_LESS")}
                  <svg
                    fill="var(--text-100)"
                    width="24px"
                    height="24px"
                    viewBox="0 0 1024 1024"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path d="M759.2 419.8L697.4 358 512 543.4 326.6 358l-61.8 61.8L512 667z" />
                  </svg>
                </>
              ) : (
                <>
                  {t("SHOP.BRANDS.SEE_MORE")}
                  <svg
                    fill="var(--text-100)"
                    width="24px"
                    height="24px"
                    viewBox="0 0 1024 1024"
                    xmlns="http://www.w3.org/2000/svg"
                    style={{ transform: "rotate(180deg)" }} // Ispravljeno
                  >
                    <path d="M759.2 419.8L697.4 358 512 543.4 326.6 358l-61.8 61.8L512 667z" />
                  </svg>
                </>
              )}
            </Button>

            <BrandsContainer>
              {categories
                .slice(0, showAll ? categories.length : 10)
                .map((category, index) => (
                  <BrandLogo
                    onClick={() => navigate(goToCategory(category.slug))}
                    key={index}
                  >
                    {category.name}
                  </BrandLogo>
                ))}
            </BrandsContainer>
            {categories.length > 9 && (
              <>
                <p style={{ textAlign: "center", margin: "8px 0" }}>
                  {/* Showing {!showAll ? "10" : categories.length} of{" "}
                  {categories.length} categories */}
                  {t("SHOP.BRANDS.SHOWING_CATEGORIES", {
                    count: "10",
                    total: categories.length,
                  })}
                </p>
                <ShowMoreButton onClick={handleShowAll}>
                  {showAll
                    ? t("SHOP.BRANDS.SHOW_LESS")
                    : t("SHOP.BRANDS.SHOW_ALL")}
                </ShowMoreButton>
              </>
            )}
          </BrandsSection>

          {categories.slice(0, visibleCount).map((category) => {
            const prods = brandData[category.slug] || [];
            return (
              <div key={category.slug} style={{ marginBottom: "30px" }}>
                <h2 style={{ textTransform: "capitalize" }}>{category.slug}</h2>
                <ProductGrid>
                  {prods.slice(0, isMobile ? 6 : 5).map((product, i) => (
                    <ProductCard key={i} product={product} />
                  ))}
                  {prods.length > (isMobile ? 6 : 5) && (
                    <LoadMoreButton
                      onClick={() => navigate(goToCategory(category.slug))}
                    >
                      {isMobile ? (
                        t("SHOP.BRANDS.MORE_PRODUCTS", {
                          count: prods.length - (isMobile ? 6 : 5),
                        })
                      ) : (
                        <>
                          <ButtonText>{prods.length - 5}+</ButtonText>
                          {t("SHOP.BRANDS.VIEW_ALL_PRODUCTS")}
                        </>
                      )}
                    </LoadMoreButton>
                  )}
                </ProductGrid>
              </div>
            );
          })}
        </Section>
      </Container>
      <Footer />
    </>
  );
};

export default BrandsMain;

// Glavni kontejner za sekciju
const BrandsSection = styled.div``;

// Stil za naslov
const Title = styled.h2`
  font-size: 2rem;
  font-weight: bold;
  margin-bottom: 20px;
`;

// Stil za opis sa glass efekatom i delimičnim sakrivanjem
const Description = styled.p`
  max-height: ${(props) =>
    props.showAll ? "none" : "70px"}; // Skriva deo teksta
  overflow: hidden;
  position: relative;
  transition: max-height 0.3s ease-in-out;
  max-height: ${(props) =>
    props.$showMore ? "100vh" : "calc(4 * var(--font-size-base))"};

  &::after {
    content: "";
    position: absolute;

    left: 0;
    right: 0;
    top: 0;
    box-shadow: 0px 15px 24px 30px rgba(251, 251, 251, 0.75);
    -webkit-box-shadow: 0px 15px 24px 30px rgba(251, 251, 251, 0.75);
    -moz-box-shadow: 0px 15px 24px 30px rgba(251, 251, 251, 0.75);
    display: ${(props) => (props.$showMore ? "none" : "block")};
    height: 60%;
  }
  @media (min-width: 768px) {
    max-width: 50%;
  }
`;
const Button = styled.div`
  border: none;
  background-color: transparent;
  color: var(--text-100);
  border-color: var(--text-100);
  &:hover {
    background-color: transparent;
    color: var(--text-100);
    border-color: var(--text-100);
  }
  margin: 8px 0 20px 0;
  display: flex;
  align-items: center;
`;

// Kontener za brendove
const BrandsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-start;
  gap: 4px;
  margin-bottom: 10px;

  @media (min-width: 768px) {
    gap: 12px;
  }
`;

// Stil za pojedinačne brendove
const BrandLogo = styled.div`
  width: calc(33% - 8px);
  height: 50px;
  //background-color: var(--bg-200);
  border: 1px solid var(--text-100);
  border-radius: 4px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  @media (min-width: 768px) {
    width: 120px;
  }
`;

// Dugme za prikazivanje svih brendova
const ShowMoreButton = styled.button`
  width: 100%;
  margin: 0 auto;
  background-color: transparent;
  color: var(--text-100);
  border-color: var(--text-100);
  @media (min-width: 768px) {
    width: 120px;
  }
  &:hover {
    background-color: transparent;
    color: var(--text-100);
    border-color: var(--text-100);
  }
`;

const GridCategory = styled.div`
  display: grid;
  grid-template-columns: repeat(6, 1fr);
`;
const GridItem = styled.div``;
